import { create } from 'zustand';
import Decimal from 'decimal.js';
import * as api from '../api/loyaltyPointConversionRates';
import { LoyaltyPointConversionRate, ServerResponse } from '../common/types';
import useAlert from '../hooks/useAlert';
import { formatPaymentType, PaymentType } from '../common/enums';

type LoyaltyPointConversionRatesStore = {
    hasLoaded: boolean;
    rates: LoyaltyPointConversionRate[];
    currentRate: LoyaltyPointConversionRate | null;
    loading: boolean;
    fetch: () => Promise<void>;
    getById: (id: number) => Promise<LoyaltyPointConversionRate | null>;
    create: (pointsToOneDollar: number) => Promise<LoyaltyPointConversionRate | null>;
    calculateDollarsCovered: (points: number, rate: number) => number;
    calculatePointsConsumed: (dollars: number, rate: number) => number;
}

const useLoyaltyPointConversionRates = create<LoyaltyPointConversionRatesStore>((set, get) => ({
    hasLoaded: false,
    rates: [],
    currentRate: null,
    loading: false,
    fetch: async () => {
        const result: ServerResponse<LoyaltyPointConversionRate[]> = await api.list();
        set({ loading: true, hasLoaded: true });
        if (result.data) {
            const currentRate = result.data.length ? result.data[0] : null;
            set({ rates: result.data || [], currentRate });
        }
        set({ loading: false });
    },
    getById: async (id: number) => {
        set({ loading: true });
        try {
            const result: ServerResponse<LoyaltyPointConversionRate> = await api.getById(id);
            if (result.data) {
                return result.data
            }
            useAlert.getState().showAlert(`${formatPaymentType(PaymentType.LoyaltyPoints)} conversion rate not found`, 'error')
            return null
        } catch (err) {
            useAlert.getState().showAlert(`${formatPaymentType(PaymentType.LoyaltyPoints)} conversion rate not found`, 'error')
            console.error(err);
            return null
        } finally {
            set({ loading: false });
        }
    },
    create: async (pointsToOneDollar: number) => {
        set({ loading: true });
        try {
            const result: ServerResponse<LoyaltyPointConversionRate> = await api.create(pointsToOneDollar);
            if (result.data) {
                useAlert.getState().showAlert(`${formatPaymentType(PaymentType.LoyaltyPoints)} conversion rate created`, 'success')
                await get().fetch()
                return result.data;
            }
            return null
        } catch (err) {
            console.error(err);
            useAlert.getState().showAlert(`Create ${formatPaymentType(PaymentType.LoyaltyPoints)} conversion rate failed`, 'error')
            return null
        } finally {
            set({ loading: false });
        }
    },
    calculateDollarsCovered: (points: number, rate: number) => {
        return new Decimal(points).dividedBy(rate).times(100).floor().div(100).toNumber();
    },
    calculatePointsConsumed: (dollars: number, rate: number) => {
        return new Decimal(dollars).times(rate).ceil().toNumber();
    },
}))

export default useLoyaltyPointConversionRates

useLoyaltyPointConversionRates.getState().fetch().then(() => console.info('useLoyaltyPointConversionRates initialised'));